"use client"

import React, { useEffect, useState, useCallback } from "react"
import { Loader, RefreshCw, Search } from "lucide-react"
import { toast } from "sonner"
import { debounce } from "lodash"
import { usePathname, useRouter } from "next/navigation"

import ErrorBoundary from "@/components/molecules/ErrorBoundary"
import { SearchButton } from "@/components/molecules/SearchButton"
import { Button } from "@/components/atoms/Button/button"
import { Label } from "@/components/atoms/Label/label"
import GroupCheckBox from "@/components/molecules/Groupcheckbox"
import PlaceSearchInput from "@/components/molecules/PlaceSearch"
import { PropertyTypeField } from "@/components/molecules/PropertySelect/PropertyField"
import { RoomsDropdown } from "@/components/molecules/PropertySelect/RoomsField"
import { PriceRangeField } from "@/components/molecules/PropertySelect/PriceRangeField"
import { Dropdown } from "@/components/molecules/Dropdown"

import { usePropertySearchStore } from "@/stores/usePropertySearchStore"
import { useModalStore } from "@/hooks/useModal"
import { usePropertySearch } from "@/app/hooks/usePropertySearch"
import { PropertySearchSchema } from "@/app/schema/property"
import { LocationData } from "@/stores/places"
import useLocaleStore from "@/app/stores/useLocaleStore"

export const SearchPropertyForm = () => {
  const locale = useLocaleStore((state) => state.locale)
  const pathname = usePathname();
  const router = useRouter();
  const { filters, setFilter, resetFilters, amenities, loadAmenities } = usePropertySearchStore()
  const { executeSearch } = usePropertySearch()
  const { closeModal } = useModalStore()
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const KEYWORD_OPTIONS = [
    { id: "sell", name: locale === "en" ? "Buy" : "ግዢ" },
    { id: "rent", name: locale === "en" ? "Rent" : "ኪራይ" },
  ]

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await loadAmenities()
      setIsLoading(false)
    }
    loadData()
  }, [loadAmenities])

  const debouncedSetFilter = useCallback(
    debounce((key: keyof typeof filters, value: any) => {
      setFilter(key, value)
    }, 300),
    [setFilter]
  )

  const validateForm = () => {
    const result = PropertySearchSchema.safeParse(filters)
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors
      const formatted = Object.fromEntries(
        Object.entries(fieldErrors).map(([key, messages]) => [key, messages?.[0] || "Invalid value"])
      )
      setFormErrors(formatted)
      return false
    }
    setFormErrors({})
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (!validateForm()) {
        toast.error("Please fix the errors before submitting.")
        return
      }

      await executeSearch()
      toast.success("Search completed successfully.")
      if (pathname === "/properties") {
        router.push("/properties/list");
      }
      closeModal("property")
    } catch {
      toast.error("An error occurred during search.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    resetFilters()
    setFormErrors({})
    toast.success("Filters have been reset.")
  }

  const getSelectOption = (value: number | null) => ({
    id: value?.toString() || "",
    name: value?.toString() || locale === "en" ? "All" : "ሁሉም",
  })

  return (
    <ErrorBoundary>
      <form className="relative max-w-screen-md" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 gap-y-8 font-light">
          <PropertyTypeField
            label={locale === "en" ? "Property Type" : "የንብረት አይነት"}
            className="property-search-form-field"
            value={filters.category || { id: "", name: locale === "en" ? "All" : "ሁሉም" }}
            onChange={(value) => debouncedSetFilter("category", value)}
          />

          <PlaceSearchInput
            value={filters.location?.address ?? ""}
            onChange={(location) => {
              debouncedSetFilter("location", location as LocationData)
              setFormErrors((prev) => ({ ...prev, location: "" }))
            }}
          />

          <RoomsDropdown
            label={locale === "en" ? "Bedrooms" : "መኝታ ክፍሎች"}
            className="property-search-form-field"
            value={getSelectOption(filters.bedrooms)}
            onChange={(val) => {
              debouncedSetFilter("bedrooms", val.id ? Number(val.id) : { id: "", name: locale === "en" ? "All" : "ሁሉም" })
              setFormErrors((prev) => ({ ...prev, bedrooms: "" }))
            }}
          />

          <RoomsDropdown
            label={locale === "en" ? "Bathrooms" : "መታጠቢያ ክፍሎች"}
            className="property-search-form-field"
            value={getSelectOption(filters.bathrooms)}
            onChange={(val) => {
              debouncedSetFilter("bathrooms", val.id ? Number(val.id) : { id: "", name: locale === "en" ? "All" : "ሁሉም" })
              setFormErrors((prev) => ({ ...prev, bathrooms: "" }))
            }}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader size={25} className="animate-spin" />
          </div>
        ) : (
          <GroupCheckBox
            options={amenities}
            selectedOptions={filters.amenities}
            onChange={(ids) => setFilter("amenities", ids)}
            label={locale === "en" ? "Amenities" : "አገልግሎች"}
            dashboardInput={false}
            list={false}
          />
        )}

        <div className="mb-8 grid grid-cols-2 gap-4 gap-y-8">
          <PriceRangeField
            label={locale === "en" ? "Price in range" : "የዋጋ ክልል"}
            className="property-search-form-field"
            value={{
              id: "",
              name:
                filters.minPrice || filters.maxPrice
                  ? `${filters.minPrice ? `$${filters.minPrice.toLocaleString()}` : locale === "en" ? "Any" : "ሁሉም"} - ${filters.maxPrice ? `$${filters.maxPrice.toLocaleString()}` : locale === "en" ? "Any" : "ሁሉም"}`
                  : locale === "en" ? "Any" : "ሁሉም",
              min: filters.minPrice ?? undefined,
              max: filters.maxPrice ?? undefined,
            }}
            onChange={(range) => {
              setFilter("minPrice", range?.min ?? null)
              setFilter("maxPrice", range?.max ?? null)
            }}
          />

          <Dropdown
            label={locale === "en" ? "Keyword" : "አይነቶች"}
            className="property-search-form-field"
            items={KEYWORD_OPTIONS}
            value={filters.keyword || KEYWORD_OPTIONS[1]}
            onChange={(value) => {
              if (filters.keyword?.id !== value.id) {
                setFilter("minPrice", null)
                setFilter("maxPrice", null)
              }
              setFilter("keyword", value)
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <SearchButton
            icon={isSubmitting ? <Loader size={25} className="animate-spin" /> : <Search size={25} />}
            label={isSubmitting ? locale === "en" ? "Searching..." : "በፍለጋ ላይ..." : locale === "en" ? "Search" : "ፈልግ"}
            className="property-search-primary"
            type="submit"
            disabled={isSubmitting}
          />

          <Button
            type="button"
            onClick={handleReset}
            variant="ghost"
            className="group flex items-center py-5"
            disabled={isSubmitting}
          >
            <RefreshCw size={15} />
            <Label className="pl-2 font-thin text-gray-500 hover:underline">{locale === "en" ? "Reset Filter" : "አጥፉ አይነቶች"}</Label>
          </Button>
        </div>
      </form>
    </ErrorBoundary>
  )
}
